/**
 * Semester & Confirmation endpoints for liveOverviewApi.
 * Extracted for maintainability — ~350 lines.
 */
import { supabase } from '../../../../lib/supabase';
import { liveOverviewApi } from './liveOverviewApi';
import type {
    SemesterInfo,
    DraftItemInfo,
    ConfirmationInfo,
    UnenrollmentRpcRow,
    AdminConfirmSettleParams,
} from '../types';

const semesterApi = liveOverviewApi.injectEndpoints({
    endpoints: (builder) => ({
        listSemesters: builder.query<SemesterInfo[], void>({
            async queryFn() {
                try {
                    const { data, error } = await supabase.rpc('list_semesters_for_school');
                    if (error) {
                        return { error: { status: 'CUSTOM_ERROR', error: error.message } };
                    }
                    const mapped = (data || []).map((s: Record<string, unknown>) => ({
                        id: String(s.id),
                        name: String(s.name || ''),
                        start_date: String(s.start_date || ''),
                        end_date: String(s.end_date || ''),
                        is_active: !!s.is_active,
                        is_current: !!s.is_current,
                        is_future: !!s.is_future,
                        has_current_draft: s.has_current_draft === true,
                    }));
                    const filtered = mapped.filter((s: SemesterInfo) => !s.is_future || s.has_current_draft);
                    return { data: filtered };
                } catch (e) {
                    return { error: { status: 'CUSTOM_ERROR', error: String(e) } };
                }
            },
            providesTags: ['Semesters'],
            keepUnusedDataFor: 300,
        }),

        getSemesterWithLatestConfirmations: builder.query<string | null, { schoolId: string }>({
            async queryFn({ schoolId }) {
                try {
                    if (!schoolId) return { data: null };
                    const { data, error } = await supabase.rpc('get_semester_with_latest_confirmations', {
                        p_school_id: schoolId,
                    });
                    if (error) {
                        return { error: { status: 'CUSTOM_ERROR', error: error.message } };
                    }
                    return { data: data || null };
                } catch (e) {
                    return { error: { status: 'CUSTOM_ERROR', error: String(e) } };
                }
            },
        }),

        getDraftItems: builder.query<DraftItemInfo[], { semesterId: string }>({
            async queryFn({ semesterId }) {
                try {
                    if (!semesterId) return { data: [] };
                    const { data, error } = await supabase.rpc('get_current_draft_items_for_semester', {
                        p_semester_id: semesterId,
                    });
                    if (error) {
                        return { error: { status: 'CUSTOM_ERROR', error: error.message } };
                    }
                    const mapped = (data || []).map((d: Record<string, unknown>) => ({
                        student_id: String(d.student_id),
                        course_id: String(d.course_id),
                        course_name: String(d.course_name || 'Unbekannt'),
                        day_index: Number(d.day_index),
                    }));
                    return { data: mapped };
                } catch (e) {
                    return { error: { status: 'CUSTOM_ERROR', error: String(e) } };
                }
            },
            providesTags: (_r, _e, { semesterId }) => [{ type: 'DraftItems', id: semesterId }],
        }),

        getConfirmationsForSemester: builder.query<
            ConfirmationInfo[],
            { schoolId: string; semesterId: string; childIds: string[] }
        >({
            async queryFn({ schoolId, semesterId, childIds }) {
                try {
                    if (!schoolId || !semesterId || childIds.length === 0) {
                        return { data: [] };
                    }
                    const { data, error } = await supabase.rpc('get_confirmations_for_semester', {
                        p_school_id: schoolId,
                        p_semester_id: semesterId,
                        p_child_profile_ids: childIds,
                    });
                    if (error) {
                        return { error: { status: 'CUSTOM_ERROR', error: error.message } };
                    }
                    return { data: (data || []) as ConfirmationInfo[] };
                } catch (e) {
                    return { error: { status: 'CUSTOM_ERROR', error: String(e) } };
                }
            },
            providesTags: (_r, _e, { semesterId }) => [{ type: 'Confirmations', id: semesterId }],
            keepUnusedDataFor: 60,
        }),

        getUnenrollmentsForSemester: builder.query<
            UnenrollmentRpcRow[],
            { schoolId: string; semesterId: string }
        >({
            async queryFn({ schoolId, semesterId }) {
                try {
                    if (!schoolId || !semesterId) return { data: [] };
                    const { data, error } = await supabase.rpc('get_unenrollments_for_semester', {
                        p_school_id: schoolId,
                        p_semester_id: semesterId,
                    });
                    if (error) {
                        return { error: { status: 'CUSTOM_ERROR', error: error.message } };
                    }
                    return { data: (data || []) as UnenrollmentRpcRow[] };
                } catch (e) {
                    return { error: { status: 'CUSTOM_ERROR', error: String(e) } };
                }
            },
            providesTags: (_r, _e, { semesterId }) => [{ type: 'Unenrollments', id: semesterId }],
        }),

        adminConfirmSettle: builder.mutation<void, AdminConfirmSettleParams>({
            async queryFn({ schoolId, childId, confirmedAt, comment }) {
                try {
                    const { error } = await supabase.rpc('admin_confirm_settle_simple' as never, {
                        p_school_id: schoolId,
                        p_child_id: childId,
                        p_confirmed_at: confirmedAt,
                        p_comment: comment,
                    } as never);
                    if (error) {
                        return { error: { status: 'CUSTOM_ERROR', error: error.message } };
                    }
                    return { data: undefined };
                } catch (e) {
                    return { error: { status: 'CUSTOM_ERROR', error: String(e) } };
                }
            },
            invalidatesTags: ['Confirmations'],
        }),
    }),
    overrideExisting: false,
});

export const {
    useListSemestersQuery,
    useGetSemesterWithLatestConfirmationsQuery,
    useGetDraftItemsQuery,
    useGetConfirmationsForSemesterQuery,
    useGetUnenrollmentsForSemesterQuery,
    useAdminConfirmSettleMutation,
} = semesterApi;
